import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Loader2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

export default function StaffAdmin() {
  const { user: current } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setRows(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
      await load();
      toast.success(`${u.name} ${u.is_active ? "deactivated" : "activated"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      await load();
      toast.success("Role updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      await load();
      toast.success("User deleted");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fade-in" data-testid="staff-admin-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">Admin</div>
          <h2 className="font-serif text-3xl text-njas-navy">Staff Management</h2>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-staff-btn" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2.5 text-sm font-medium hover:bg-njas-navy-hover">
          <Plus className="w-4 h-4" /> Add staff
        </button>
      </div>

      <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-njas-bg-alt text-njas-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Role</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Created</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-njas-muted">Loading…</td></tr>}
              {!loading && rows.map((u) => (
                <tr key={u.id} className="border-t border-[var(--njas-border)]" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3">{u.name}</td>
                  <td className="px-5 py-3 text-njas-muted">{u.email}</td>
                  <td className="px-5 py-3">
                    <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} disabled={u.id === current?.id} className="rounded-md border border-[var(--njas-border)] bg-white text-xs py-1.5 px-2 capitalize">
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                      {u.is_active ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-njas-muted">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-AU") : "—"}</td>
                  <td className="px-5 py-3 text-right space-x-2">
                    <button onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`} className="inline-flex items-center gap-1 text-xs text-njas-navy hover:underline">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    {u.id !== current?.id && (
                      <>
                        <button onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`} className="text-xs text-njas-navy hover:underline">
                          {u.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="text-xs text-red-600 hover:underline">
                          Delete
                        </button>
                      </>
                    )}
                    {u.id === current?.id && <span className="text-xs text-njas-muted">(you)</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && <AddStaffModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
      {editing && <EditStaffModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditStaffModal({ user, onClose, onSaved }) {
  const [f, set] = useState({ name: user.name || "", email: user.email || "", role: user.role || "staff", password: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, email: f.email, role: f.role };
      if (f.password) body.password = f.password;
      await api.patch(`/admin/users/${user.id}`, body);
      toast.success("Staff details updated");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" onClick={onClose} data-testid="edit-staff-modal">
      <div className="min-h-screen bg-white p-6 sm:p-10" onClick={(e) => e.stopPropagation()}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="eyebrow text-njas-sage-hover text-[10px]">Admin</div>
              <h3 className="font-serif text-3xl text-njas-navy">Edit staff details</h3>
            </div>
            <button onClick={onClose} data-testid="close-edit-modal" className="p-2 rounded-full hover:bg-njas-bg-alt text-njas-muted hover:text-njas-navy"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="edit-staff-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-sm font-medium text-njas-text">Name</span>
                <input required value={f.name} onChange={upd("name")} className={`${inputCls} mt-1.5`} data-testid="edit-staff-name" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-njas-text">Email</span>
                <input required type="email" value={f.email} onChange={upd("email")} className={`${inputCls} mt-1.5`} data-testid="edit-staff-email" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-njas-text">Role</span>
                <select value={f.role} onChange={upd("role")} className={`${inputCls} mt-1.5`} data-testid="edit-staff-role">
                  <option value="staff">Staff</option>
                  <option value="peer_worker">Peer worker</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-njas-text">Reset password <span className="text-njas-muted font-normal">(optional)</span></span>
                <input type="text" value={f.password} onChange={upd("password")} placeholder="Leave blank to keep current" className={`${inputCls} mt-1.5`} data-testid="edit-staff-password" />
                <span className="text-xs text-njas-muted mt-1 block">Fill in only if you want to reset this user&apos;s password.</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-[var(--njas-border)]">
              <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
              <button type="submit" disabled={busy} data-testid="edit-staff-submit" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm font-medium hover:bg-njas-navy-hover disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function AddStaffModal({ onClose, onCreated }) {
  const [f, set] = useState({ name: "", email: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", f);
      toast.success("Staff account created");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" onClick={onClose} data-testid="add-staff-modal">
      <div className="min-h-screen bg-white p-6 sm:p-10" onClick={(e) => e.stopPropagation()}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="eyebrow text-njas-sage-hover text-[10px]">Admin</div>
              <h3 className="font-serif text-3xl text-njas-navy">Add staff account</h3>
            </div>
            <button onClick={onClose} data-testid="close-add-modal" className="p-2 rounded-full hover:bg-njas-bg-alt text-njas-muted hover:text-njas-navy"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="add-staff-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-sm font-medium text-njas-text">Name</span>
                <input required value={f.name} onChange={upd("name")} className={`${inputCls} mt-1.5`} data-testid="add-staff-name" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-njas-text">Email</span>
                <input required type="email" value={f.email} onChange={upd("email")} className={`${inputCls} mt-1.5`} data-testid="add-staff-email" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-sm font-medium text-njas-text">Temporary password</span>
                <input required type="text" minLength={8} value={f.password} onChange={upd("password")} className={`${inputCls} mt-1.5`} data-testid="add-staff-password" />
                <span className="text-xs text-njas-muted mt-1 block">Minimum 8 characters. Ask them to change it after first login.</span>
              </label>
              <label className="block sm:col-span-2">
                <span className="text-sm font-medium text-njas-text">Role</span>
                <select value={f.role} onChange={upd("role")} className={`${inputCls} mt-1.5`} data-testid="add-staff-role">
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-[var(--njas-border)]">
              <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
              <button type="submit" disabled={busy} data-testid="add-staff-submit" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm font-medium hover:bg-njas-navy-hover disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create account
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
